import { useState, useEffect } from 'react';
import axios from 'axios';
import { X, Bell, AlertTriangle, Info, Megaphone } from 'lucide-react';
import { Button } from '@/components/ui/button';

const FloatingNotice = () => {
  const [notice, setNotice] = useState(null);
  const [isVisible, setIsVisible] = useState(true);
  const [isMinimized, setIsMinimized] = useState(false);
  const [isAnimating, setIsAnimating] = useState(true);

  useEffect(() => {
    fetchNotice();
  }, []);

  useEffect(() => {
    if (notice?.auto_dismiss && notice?.dismiss_after_seconds) {
      const timer = setTimeout(() => {
        setIsVisible(false);
      }, notice.dismiss_after_seconds * 1000);
      return () => clearTimeout(timer);
    }
  }, [notice]);

  // Pulse animation effect
  useEffect(() => {
    const interval = setInterval(() => {
      setIsAnimating(prev => !prev);
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  const fetchNotice = async () => {
    try {
      const response = await axios.get('/notice');
      if (response.data?.enabled) {
        setNotice(response.data);
        // Check if user has dismissed this notice in this session
        const dismissedNotice = sessionStorage.getItem('dismissed_notice');
        if (dismissedNotice === response.data.updated_at) {
          setIsVisible(false);
        }
      } else {
        setNotice(null);
      }
    } catch (error) {
      // Notice endpoint may return empty/error if no notice exists
      setNotice(null);
    }
  };

  const handleDismiss = () => {
    setIsVisible(false);
    if (notice?.updated_at) {
      sessionStorage.setItem('dismissed_notice', notice.updated_at);
    }
  };

  const getTypeStyles = () => {
    switch (notice?.type) {
      case 'urgent':
        return {
          bg: 'bg-gradient-to-r from-red-500 to-rose-600',
          border: 'border-red-400',
          icon: <AlertTriangle className="w-5 h-5" />,
          pulse: 'animate-pulse'
        };
      case 'warning':
        return {
          bg: 'bg-gradient-to-r from-amber-500 to-orange-500',
          border: 'border-amber-400',
          icon: <Bell className="w-5 h-5" />,
          pulse: ''
        };
      default:
        return {
          bg: 'bg-gradient-to-r from-blue-500 to-indigo-600',
          border: 'border-blue-400',
          icon: <Info className="w-5 h-5" />,
          pulse: ''
        };
    }
  };

  if (!notice?.enabled || !isVisible) {
    return null;
  }

  const styles = getTypeStyles();

  if (isMinimized) {
    return (
      <div className="fixed bottom-4 right-4 z-50" data-testid="floating-notice-minimized">
        <button
          onClick={() => setIsMinimized(false)}
          className={`${styles.bg} text-white p-3 rounded-full shadow-lg hover:scale-110 transition-all duration-300 ${isAnimating ? 'animate-bounce' : ''}`}
          data-testid="floating-notice-expand-btn"
        >
          <Megaphone className="w-6 h-6" />
        </button>
      </div>
    );
  }

  return (
    <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:max-w-md z-50 animate-in slide-in-from-bottom-5 duration-500" data-testid="floating-notice">
      <div className={`${styles.bg} ${styles.pulse} rounded-xl shadow-2xl overflow-hidden`} data-testid={`floating-notice-type-${notice.type}`}>
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 bg-black/10">
          <div className="flex items-center gap-2 text-white">
            {styles.icon}
            <span className="font-bold text-sm uppercase tracking-wide" data-testid="floating-notice-title">{notice.title}</span>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setIsMinimized(true)}
              className="text-white/80 hover:text-white p-1 hover:bg-white/10 rounded transition-colors"
              title="Minimize"
              data-testid="floating-notice-minimize-btn"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
              </svg>
            </button>
            {notice.show_close_button && (
              <button
                onClick={handleDismiss}
                className="text-white/80 hover:text-white p-1 hover:bg-white/10 rounded transition-colors"
                title="Dismiss"
                data-testid="floating-notice-close-btn"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
        
        {/* Content */}
        <div className="px-4 py-3 text-white" data-testid="floating-notice-content">
          <div 
            className="text-sm leading-relaxed prose prose-invert prose-sm max-w-none"
            dangerouslySetInnerHTML={{ __html: notice.content.replace(/\n/g, '<br/>') }}
          />
        </div>
        
        {/* Footer decoration */}
        <div className="h-1 bg-gradient-to-r from-white/0 via-white/30 to-white/0" />
      </div>
    </div>
  );
};

export default FloatingNotice;
